// The hover action row on a Phoebe answer: thumbs up/down feedback, copy,
// and a platform-admin jump to the message's raw event in the debug rail.
// Feedback needs the committed event's durable agent item id, so the
// thumbs render only once the message has been committed by the stream.
import { Bug } from 'lucide-react';
import { Fragment, type ReactNode } from 'react';
import useSWR from 'swr';

import { MessageFeedbackControls } from '@apps/web/components/message_feedback_controls';
import { Button } from '@apps/web/components/ui/button';
import {
    fetchRunMessageFeedback,
    updateRunMessageFeedback,
} from '@apps/web/features/phoebe_chat_v3/api/run_api';
import { type HarnessScope } from '@apps/web/features/phoebe_chat_v3/model/event_types';
import { type RegistryOccurrence } from '@apps/web/features/phoebe_chat_v3/registry/types';
import { useHarnessScope } from '@apps/web/features/phoebe_chat_v3/ui/harness_scope_context';
import { useOpenMessageDebug } from '@apps/web/features/phoebe_chat_v3/ui/message_debug_context';
import { useApiQuery } from '@apps/web/swr_data_fetching/use_api_query';

export const MESSAGE_ACTION_BUTTON_CLASS =
    'text-muted-foreground hover:text-foreground size-6 rounded-[5px]';

function MessageFeedback({
    scope,
    agentItemId,
}: Readonly<{
    readonly scope: HarnessScope;
    readonly agentItemId: string;
}>): ReactNode {
    const feedbackKey = [
        'v3-run-message-feedback',
        scope.userId,
        scope.organizationId,
        scope.mode,
        scope.runId,
    ] as const;
    const { data: feedbackList, mutate } = useSWR(
        feedbackKey,
        () => fetchRunMessageFeedback(scope),
        // Feedback is decorative: a failed load just shows no prior rating.
        // The translated error carries no HTTP status for the global SWR
        // handler to classify, so it would otherwise become a generic toast
        // on every refetch (PHO-17299).
        { onError: () => undefined },
    );
    const feedback =
        feedbackList?.find((entry) => entry.agent_item_id === agentItemId) ??
        null;
    return (
        <MessageFeedbackControls
            feedback={feedback}
            requiresComment={false}
            onSubmit={async (submission) => {
                try {
                    const saved = await updateRunMessageFeedback(scope, {
                        agentItemId,
                        label: submission.label,
                        comment: submission.comment,
                    });
                    void mutate(
                        (current) => [
                            ...(current ?? []).filter(
                                (entry) => entry.agent_item_id !== agentItemId,
                            ),
                            saved,
                        ],
                        { revalidate: false },
                    );
                    return true;
                } catch {
                    return false;
                }
            }}
        />
    );
}

function AdminDebugEventButton({
    eventId,
}: Readonly<{ readonly eventId: string }>): ReactNode {
    const openMessageDebug = useOpenMessageDebug();
    const viewer = useApiQuery({
        path: '/users/me',
        method: 'get',
        reason: 'v3-run-message-actions',
        select: (response) => response.user,
    });
    if (viewer.data?.admin !== true || openMessageDebug === null) {
        return null;
    }
    return (
        <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label="Show event in debug panel"
            title="Show event in debug panel"
            className={MESSAGE_ACTION_BUTTON_CLASS}
            onClick={() => openMessageDebug(eventId)}
        >
            <Bug aria-hidden="true" className="size-3.5" />
        </Button>
    );
}

export function AssistantMessageActions({
    occurrence,
    copyControl,
}: Readonly<{
    readonly occurrence: RegistryOccurrence<'assistant_text' | 'user_message'>;
    readonly copyControl: ReactNode;
}>): ReactNode {
    const scope = useHarnessScope();
    const agentItemId = occurrence.committed?.agent_item_id ?? null;
    // The debug jump targets the committed event id, so it appears only
    // once the message exists in the raw event timeline.
    const eventId = occurrence.committed?.id ?? null;
    return (
        <Fragment>
            {scope !== null && agentItemId !== null && (
                <MessageFeedback scope={scope} agentItemId={agentItemId} />
            )}
            {copyControl}
            {scope !== null && eventId !== null && (
                <AdminDebugEventButton eventId={eventId} />
            )}
        </Fragment>
    );
}
